import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ClipboardList, Download, Droplets, FlaskConical, Pencil, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const STATUS_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'C', label: 'Cumple' },
  { value: 'NC', label: 'No cumple' },
  { value: 'NA', label: 'No aplica' },
];

const INSPECTION_META = {
  sustancias_quimicas: {
    title: 'Sustancias químicas',
    description: 'Control e inspección de almacenamiento, rotulado y manejo seguro de sustancias químicas.',
    icon: FlaskConical,
    gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
    formCode: 'FT-AMB-089',
    version: '0',
    headerDateLabel: 'VIGENCIA:',
    headerDateValue: '2025-07-01',
    listColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'inspection_place', label: 'Lugar' },
      { key: 'responsible_name', label: 'Responsable' },
      { key: 'responsible_role', label: 'Cargo' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true },
          { key: 'inspection_place', label: 'Lugar de la inspección', required: true },
        ],
      },
      {
        title: 'Responsable de inspección',
        fields: [
          { key: 'responsible_name', label: 'Nombre' },
          { key: 'responsible_role', label: 'Cargo' },
        ],
      },
    ],
    checklistSections: [
      {
        title: 'Aspectos a evaluar',
        items: [
          'Cada envase de producto químico esta etiquetado como mínimo con información del nombre del producto, el pictograma correspondiente para identificar el riesgo y las actuaciones a seguir en caso de emergencia:',
          'Están disponibles las Fichas de Seguridad de los productos químicos almacenados:',
          'Las fecha de elaboración o actualización de las fichas de datos de seguridad tienen menos de 5 años de antigüedad',
          'El área de almacenamiento se encuentra señalizado: Identificación del área de almacenamiento de sustancias químicas.',
          'Los productos químicos almacenados se encuentran cerrados o sellados, herméticamente:',
          'Cuando se realiza reenvase de productos químicos no se utilizan recipientes provenientes de alimentos',
          'Los productos químicos almacenados se encuentran en áreas de fácil acceso y manipulación:',
          'Se dispone en un lugar visible la matriz de compatibilidad química para el almacenamiento de productos químicos:',
          'La estantería se encuentra señalada y diferenciada conforme a lo indicado en la matriz de compatibilidad química para el almacenamiento de productos químicos:',
          'El área destinada para el almacenamiento de productos químicos se encuentra ventilada:',
          'El área destinada para el almacenamiento de productos químicos se encuentra protegida de la exposición a la luz solar directa, chispas, llamas, electricidad estática u otras fuentes de calor o ignición:',
          'En el tercer nivel de altura de la estantería dispuesta para el almacenamiento de productos químicos se evita almacenar productos líquidos y/o de alto riesgo:',
          'No se debe almacenar contenedores de sustancias químicas uno encima del otro ni sobre el piso donde se pueda volcar accidentalmente:',
          'Los equipos para primeros auxilios y kit antiderrames son accesibles en todo momento y ubicados en exteriores al área dispuesta para el almacenamiento de productos químicos:',
          'El almacenamiento de productos químicos se encuentra ubicado lejos de áreas de trabajo, fuentes de agua, llaves, desagües o vertederos:',
          'En el área dispuesta para el almacenamiento de productos químicos no debe almacenarse o consumir alimentos:',
          'Los envases y empaques vacíos de productos químicos son extraídos del área de almacenamiento y son dispuestos adecuadamente:',
          'El almacenamiento de productos químicos dispuesto es exclusivo para esta actividad:',
          'El área de almacenamiento de productos químicos permite la adecuada manipulación y movimiento de los colaboradores:',
          'El piso del área de almacenamiento de productos químicos es uniforme, impermeable y lavable',
          'Los productos identificados y categorizados como inflamables son almacenados a 1,5 metros de productos identificados y categorizados como oxidantes y corrosivos:',
          'Se dispone de áreas para la manipulación, maniobra, re envase de productos químicos:',
          'El área dispuesta para el almacenamiento de sustancias químicas es iluminado:',
        ],
      },
    ],
  },
  saneamiento_basico: {
    title: 'Saneamiento básico',
    description: 'Seguimiento de orden, limpieza, disposición de residuos y control de vectores y plagas.',
    icon: Droplets,
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
    formCode: 'FT-AMB-091',
    version: '01',
    headerDateLabel: 'FECHA:',
    headerDateValue: '2025-07-01',
    listColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'project_name', label: 'Proyecto' },
      { key: 'inspection_place', label: 'Lugar' },
      { key: 'city_name', label: 'Ciudad' },
      { key: 'inspector_name_role', label: 'Inspector' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'project_name', label: 'Proyecto' },
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true },
          { key: 'inspection_place', label: 'Lugar', required: true },
          { key: 'city_name', label: 'Ciudad' },
        ],
      },
      {
        title: 'Responsable',
        fields: [
          { key: 'inspector_name_role', label: 'Nombre y cargo de quien realiza la inspección' },
        ],
      },
    ],
    checklistSections: [
      {
        title: 'Manejo de residuos sólidos y peligrosos',
        items: [
          'Contenedores y/o canecas de basura cuentan con tapa para cerrar de forma adecuada',
          'Contenedores y/o canecas de basura cuentan con bolsa plástica',
          'Contenedores y/o canecas de basura se encuentran alejados de las puertas',
          'Las canecas y bolsas cumplen con el código de colores',
          'Contenedores y/o canecas de basura se encuentran sobre superficies que se puedan limpiar (concreto)',
          'Las áreas alrededor de los contenedores y/o canecas están libres de derrame de líquidos o residuos',
          'El área de manejo y clasificación de residuos sólidos se encuentra señalizado: Identificación del área de punto ecológico.',
          'El área de manejo y clasificación de residuos peligrosos se encuentra señalizado: Identificación del área de acopio temporal.',
          'Evidencia de plagas y/o daños a materiales almacenados',
        ],
      },
      {
        title: 'Zonas Verdes o Despejadas',
        items: [
          'Se encuentran las áreas verdes podadas y limpias',
          'Las fuentes de agua (grifos) no generan agua estancada',
          'Las fuentes de agua están libres de goteras o fugas',
          'Las paredes, los techos y los cimientos de la estructura se encuentran en buen estado, sin la presencia de huecos, grietas ni rajaduras que puedan facilitar la entrada o proliferación de vectores y plagas.',
          'Los equipos expuestos a la intemperie se encuentran en buen estado y libres de acumulaciones de agua estancada que puedan favorecer la proliferación de vectores y plagas.',
          'Las canaletas de los techos están sin hojas, desechos o almacenamiento de aguas',
          'Las paredes del edificio están libres de hiedras u otras enredaderas',
          'Las áreas y pasillos se encuentran limpios, organizados y libres de desorden, garantizando condiciones adecuadas para prevenir la acumulación de residuos que puedan atraer vectores y plagas.',
        ],
      },
      {
        title: 'Cocina',
        items: [
          'Las áreas situadas alrededor y debajo del lavaplatos y del refrigerador se mantienen limpias y secas, eliminando condiciones que puedan favorecer la presencia o proliferación de vectores y plagas.',
          'Los estantes, armarios y cajones se encuentran limpios y secos, lo que contribuye a prevenir la acumulación de suciedad o humedad que pueda favorecer la aparición de vectores y plagas.',
          'Los hornos se encuentran completamente limpios, sin restos de comida, grasa ni sustancias dulces que puedan atraer vectores o plagas.',
          'Los pisos se mantienen limpios y completamente libres de restos de comida, grasa o sustancias dulces que puedan atraer la presencia de vectores y plagas.',
          'Se ha identificado la presencia de cajas de cartón u otros elementos de empaquetado innecesarios, los cuales deben ser retirados para evitar la acumulación de materiales que puedan atraer vectores y plagas.',
          'Las fuentes de agua, como grifos y tuberías, se encuentran en óptimas condiciones, sin presentar goteras ni fugas que puedan contribuir a problemas de acumulación de humedad o proliferación de vectores y plagas.',
          'Se han encontrado evidencias de plagas, como indicios visibles o daños en el material almacenado, lo cual requiere una evaluación y acciones correctivas para controlar su proliferación.',
          'Las canecas o contenedores de residuos están equipados con tapa y bolsa plástica, garantizando un manejo adecuado de los desechos y evitando posibles condiciones que favorezcan la aparición de vectores y plagas.',
        ],
      },
      {
        title: 'Almacen',
        items: [
          '¿Los productos almacenados se encuentran organizados, etiquetados y en condiciones óptimas?',
          '¿Hay evidencia de plagas, como daños en los empaques o residuos asociados a su presencia?',
          '¿Se utilizan estanterías adecuadas que permitan evitar el contacto directo de los productos con el suelo?',
          '¿Los botes de para residuós tienen tapas y bolsas plásticas para evitar el acceso de plagas?.',
          '¿El área de almacenamiento está libre de humedad, suciedad o acumulación de polvo?',
          '¿El área de almacenamiento de material de reintegro se encuentra organizado de acuerdo con el tipo de material?',
          '¿Se lleva un control del ingreso y salida de materiales para prevenir acumulaciones innecesarias?',
        ],
      },
      {
        title: 'Parqueadero',
        items: [
          '¿El parqueadero está limpio y libre de acumulación de basura o restos de comida?',
          '¿Se han identificado charcos de agua estancada que puedan favorecer la proliferación de vectores?',
          '¿Las zonas de drenaje y alcantarillas están en buenas condiciones y libres de obstrucciones?',
          '¿Existen grietas o huecos en las estructuras del parqueadero que puedan convertirse en refugio de plagas?',
          '¿Se realiza mantenimiento periódico en las áreas comunes del parqueadero para prevenir problemas de higiene?',
        ],
      },
      {
        title: 'Baños',
        items: [
          '¿Las áreas debajo de los lavamanos están limpias, secas y libres de acumulación de agua?',
          '¿Las fuentes de agua, como grifos y tuberías, están en buen estado, sin presentar fugas o goteras?',
          '¿Existen señales visibles de plagas, como excrementos, nidos o daños en los materiales?',
          '¿Los botes de basura tienen tapas y bolsas plásticas para evitar el acceso de plagas?.',
          '¿Se realiza limpieza frecuente en los baños para mantenerlos libres de migas, residuos y desperdicios?',
          '¿Las áreas detrás y alrededor de los inodoros están limpias y sin humedad?',
          '¿Se cuenta con un registro documentado que evidencie las actividades de limpieza realizadas en los baños?',
        ],
      },
      {
        title: 'Espacios comunes',
        items: [
          '¿Las áreas comunes se encuentran limpias y libres de acumulación de residuos o desechos?',
          'Los espacios entre las paredes están libres de agujeros, grietas',
          'Los artículos de aseo están almacenados en lugares adecuados',
          'Los espacios comunes y de oficinas se encuentran libres de envases de bebidas, migas y desperdicios, asegurando condiciones higiénicas que previenen la atracción de vectores y plagas.',
          '¿Se realizan limpiezas periódicas de los muebles y esquinas de los espacios comunes?',
          'Se guardan registros cuando se informan existencias de plagas',
        ],
      },
    ],
  },
  kit_derrames: {
    title: 'Kit derrames',
    description: 'Verificación de disponibilidad, estado y uso adecuado del kit para atención de derrames.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)',
    formCode: 'FT-AMB-092',
    version: '00',
    headerDateLabel: 'FECHA:',
    headerDateValue: '2025-07-24',
    listColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'responsible_kit_name', label: 'Responsable kit' },
      { key: 'inspection_place', label: 'Lugar' },
      { key: 'vehicle_plate', label: 'Placa vehículo' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'responsible_kit_name', label: 'Nombre responsable del kit' },
          { key: 'responsible_kit_role', label: 'Cargo' },
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true },
          { key: 'inspection_place', label: 'Lugar de inspección', required: true },
          { key: 'vehicle_plate', label: 'Placa vehículo' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'performed_by_name', label: 'Realizada por' },
          { key: 'performed_by_role', label: 'Cargo de quien realiza' },
          { key: 'received_by_name', label: 'Recibida por' },
          { key: 'received_by_role', label: 'Cargo de quien recibe' },
        ],
      },
    ],
    checklistSections: [
      {
        title: 'Kit de derrame',
        items: [
          'MALETÍN O CONTENEDOR',
          'CINTA DE SEÑALIZACIÓN',
          'BARRERAS ABSORBENTES',
          'PAÑOS ABSORBENTES',
          'ABSORBENTE INDUSTRIAL GRANULADO',
          'ESCOBA DE CEPILLO INDUSTRIAL (NO METÁLICO)',
          'RECOGEDOR TIPO INDUSTRIAL (NO METÁLICO)',
          'BOLSAS ROJAS DE TIPO INDUSTRIAL',
          'ETIQUETAS ADHESIVAS PARA MARCAR LOS RESIDUOS',
          'AMARRAS PLÁSTICAS',
          'DESENGRASANTE',
          'MONOGAFAS DE SEGURIDAD',
          'PROTECCIÓN RESPIRATORIA',
          'GUANTES DE NITRILO',
          'TRAJE TIBECK',
          'MASILLA EPOXÍCA',
        ],
      },
      {
        title: 'Condiciones generales',
        items: [
          'UBICADO EN UN LUGAR DE FÁCIL ACCESO',
          'LIMPIO Y ORDENADO',
          'CONTENEDOR DEL KIT ROTULADO',
          'FICHAS DE DATOS DE SEGURIDAD ACTUALIZADAS',
          'OPERADOR TIENE CONOCIMIENTO DEL KIT',
        ],
      },
    ],
  },
  respel: {
    title: 'RESPEL',
    description: 'Inspección de transporte y cumplimiento documental del gestor externo de residuos peligrosos.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #059669 0%, #16A34A 100%)',
    formCode: 'FT-AMB-093',
    version: '00',
    headerDateLabel: 'FECHA:',
    headerDateValue: '2025-07-01',
    listColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'collector_company', label: 'Empresa recolectora' },
      { key: 'inspection_place', label: 'Lugar' },
      { key: 'vehicle_plate', label: 'Placa vehículo' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'collector_company', label: 'Empresa recolectora' },
          { key: 'inspection_date', label: 'Fecha de la inspección', type: 'date', required: true },
          { key: 'vehicle_plate', label: 'Placa vehículo' },
          { key: 'inspection_place', label: 'Lugar', required: true },
        ],
      },
      {
        title: 'Inspección realizada por',
        fields: [
          { key: 'performed_by_name', label: 'Nombre' },
          { key: 'performed_by_role', label: 'Cargo' },
          { key: 'performed_by_id', label: 'Cédula' },
        ],
      },
      {
        title: 'Inspección recibida por',
        fields: [
          { key: 'received_by_name', label: 'Nombre' },
          { key: 'received_by_role', label: 'Cargo' },
          { key: 'received_by_id', label: 'Cédula' },
        ],
      },
    ],
    checklistSections: [
      {
        title: 'Aspectos a evaluar',
        items: [
          'El personal de transporte demuestra conocimiento en manejo adecuado de residuos.',
          'El vehículo cuenta con elementos para fijar y asegurar los residuos, de tal forma que no presente un peligro para la vida de las personas y el medio ambiente.',
          '¿Los rótulos de identificación de peligrosidad de los residuos se encuentran ubicados en todas las caras visibles de la unidad de transporte y parte delantera de la cabina del vehículo?',
          '¿El vehículo cuenta con las hojas de seguridad correspondientes a la mercancía peligrosa transportada?',
          'El vehículo cuenta con la placa con el número de las Naciones Unidas con fondo color rojo',
          'El vehículo cuenta con elementos básicos para la atención de emergencias, tales como: extintor de incendios, botiquín de primeros auxilios, elementos para contención de derrames, elementos de protección personal.',
          'El vehículo porta como mínimo 2 extintores multiproposito',
          'El vehículo cuenta con documentacion legal vigentes (SOAT, TECNOMECANICA)',
          '¿El vehículo cuenta con un dispositivo sonoro o pito, que se activa cuando el vehículo se encuentra en movimiento de reversa?',
          'El personal transportador cuenta con elementos de protección personal adecuados para la manipulación de los residuos',
          '¿El personal a cargo del transporte se encuentra capacitado según la Res 1223 de 2004?',
        ],
      },
    ],
  },
};

export const INSPECCIONES_AMBIENTALES_OPTIONS = [
  { key: 'sustancias_quimicas', label: 'Sustancias químicas' },
  { key: 'saneamiento_basico', label: 'Saneamiento básico' },
  { key: 'kit_derrames', label: 'Kit derrames' },
  { key: 'respel', label: 'RESPEL' },
];

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function fmtDateDisplay(value) {
  if (!value) return '';
  const [y, m, d] = String(value).split('-');
  if (!y || !m || !d) return String(value);
  return `${d}/${m}/${y}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function toChecklistEntries(config) {
  const entries = [];
  config.checklistSections.forEach((section, sectionIndex) => {
    section.items.forEach((label, itemIndex) => {
      entries.push({
        id: `${config.formCode.toLowerCase()}_${sectionIndex + 1}_${itemIndex + 1}`,
        sectionTitle: section.title,
        label,
      });
    });
  });
  return entries;
}

function emptyInspectionForm(config) {
  const metadata = {};
  config.fieldGroups.forEach((group) => {
    group.fields.forEach((field) => {
      metadata[field.key] = field.type === 'date' ? (field.defaultValue || todayYmd()) : (field.defaultValue || '');
    });
  });
  if (!metadata.inspection_date) metadata.inspection_date = todayYmd();

  const checklist = {};
  toChecklistEntries(config).forEach((item) => {
    checklist[item.id] = { status: '', observations: '', quantity_verified: '' };
  });

  return {
    id: '',
    metadata,
    checklist,
    notes: '',
  };
}

function mergeChecklistWithConfig(config, storedChecklist) {
  const merged = {};
  const source = storedChecklist && typeof storedChecklist === 'object' ? storedChecklist : {};
  toChecklistEntries(config).forEach((item) => {
    const current = source[item.id] && typeof source[item.id] === 'object' ? source[item.id] : {};
    merged[item.id] = {
      status: current.status || '',
      observations: current.observations || '',
      quantity_verified: current.quantity_verified || '',
    };
  });
  return merged;
}

function countStatuses(checklist) {
  const base = { C: 0, NC: 0, NA: 0 };
  Object.values(checklist || {}).forEach((item) => {
    const key = item?.status;
    if (key && Object.prototype.hasOwnProperty.call(base, key)) base[key] += 1;
  });
  return base;
}

export default function InspeccionesAmbientalModule({ location, inspectionType = 'sustancias_quimicas' }) {
  const config = useMemo(() => INSPECTION_META[inspectionType] || INSPECTION_META.sustancias_quimicas, [inspectionType]);
  const checklistEntries = useMemo(() => toChecklistEntries(config), [config]);
  const [form, setForm] = useState(() => emptyInspectionForm(config));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const locationLabel = useMemo(() => {
    if (location === 'popayan') return 'Popayán';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const statusSummary = useMemo(() => countStatuses(form.checklist), [form.checklist]);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await api.sgi.inspections.list({ location, inspection_type: inspectionType, limit: 1000, order: 'desc' });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.map((row) => ({
        ...row,
        metadata: parseJsonObject(row.metadata_json),
        checklist: parseJsonObject(row.checklist_json),
      })));
    } catch (err) {
      console.error('Error cargando inspecciones ambientales', err);
      setFeedback('No se pudieron cargar las inspecciones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyInspectionForm(config));
    setFeedback('');
    loadRows();
  }, [location, inspectionType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMetadataChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: value,
      },
    }));
  };

  const onChecklistChange = (itemId, patch) => {
    setForm((prev) => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [itemId]: {
          ...(prev.checklist[itemId] || { status: '', observations: '' }),
          ...patch,
        },
      },
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const requiredField = config.fieldGroups
      .flatMap((group) => group.fields)
      .find((field) => field.required && !String(form.metadata[field.key] || '').trim());

    if (requiredField) {
      setFeedback(`Completa el campo obligatorio: ${requiredField.label}.`);
      return;
    }

    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.inspections.upsert({
        id: form.id || '',
        location,
        inspection_type: inspectionType,
        inspection_date: form.metadata.inspection_date || '',
        metadata_json: form.metadata,
        checklist_json: form.checklist,
        notes: form.notes || '',
      });
      setForm(emptyInspectionForm(config));
      setFeedback(`Registro guardado correctamente en ${config.title}.`);
      await loadRows();
    } catch (err) {
      console.error('Error guardando inspección ambiental', err);
      setFeedback(err?.message || 'No se pudo guardar la inspección.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    const metadata = { ...emptyInspectionForm(config).metadata, ...parseJsonObject(row.metadata_json || row.metadata) };
    const checklist = mergeChecklistWithConfig(config, parseJsonObject(row.checklist_json || row.checklist));
    setForm({
      id: row.id || '',
      metadata,
      checklist,
      notes: row.notes || '',
    });
    setFeedback(`Editando registro #${row.id} de ${config.title}.`);
  };

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro #${row.id} de ${config.title}?`);
    if (!ok) return;
    try {
      await api.sgi.inspections.delete({ id: row.id });
      if (String(form.id) === String(row.id)) setForm(emptyInspectionForm(config));
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      console.error('Error eliminando inspección ambiental', err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const exportExcel = async () => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const markX = (status, target) => (status === target ? 'X' : '');
      const metadataValue = (metadata, key, isDate = false) => {
        const raw = metadata?.[key] || '';
        return isDate ? fmtDateDisplay(raw) : raw;
      };

      const buildMetaRows = (metadata) => {
        if (inspectionType === 'sustancias_quimicas') {
          return `
            <tr>
              <td class="meta-label">FECHA:</td>
              <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'inspection_date', true))}</td>
              <td class="meta-label">LUGAR DE LA INSPECCION:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'inspection_place'))}</td>
            </tr>
          `;
        }
        if (inspectionType === 'saneamiento_basico') {
          return `
            <tr>
              <td class="meta-label">PROYECTO:</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'project_name'))}</td>
              <td class="meta-label">FECHA:</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'inspection_date', true))}</td>
            </tr>
            <tr>
              <td class="meta-label">LUGAR:</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'inspection_place'))}</td>
              <td class="meta-label">CIUDAD:</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'city_name'))}</td>
            </tr>
          `;
        }
        if (inspectionType === 'kit_derrames') {
          return `
            <tr>
              <td class="meta-label" colspan="3">NOMBRE RESPONSABLE DEL KIT</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'responsible_kit_name'))}</td>
              <td class="meta-label" colspan="2">CARGO</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'responsible_kit_role'))}</td>
            </tr>
            <tr>
              <td class="meta-label" colspan="2">FECHA</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'inspection_date', true))}</td>
              <td class="meta-label" colspan="4">LUGAR DE INSPECCIÓN</td>
              <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'inspection_place'))}</td>
              <td class="meta-label" colspan="1">PLACA VEHÍCULO</td>
              <td class="meta-value" colspan="1">${escapeHtml(metadataValue(metadata, 'vehicle_plate'))}</td>
            </tr>
          `;
        }
        return `
          <tr>
            <td class="meta-label" colspan="3">EMPRESA RECOLECTORA:</td>
            <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'collector_company'))}</td>
            <td class="meta-label" colspan="3">FECHA DE LA INSPECCIÓN:</td>
            <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'inspection_date', true))}</td>
          </tr>
          <tr>
            <td class="meta-label" colspan="3">PLACA VEHÍCULO:</td>
            <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'vehicle_plate'))}</td>
            <td class="meta-label" colspan="3">LUGAR:</td>
            <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'inspection_place'))}</td>
          </tr>
        `;
      };

      const buildChecklistTable = (checklist) => {
        if (inspectionType === 'kit_derrames') {
          let itemCounter = 1;
          const body = config.checklistSections.map((section) => {
            const sectionRows = section.items.map((label) => {
              const entry = checklist[checklistEntries.find((x) => x.sectionTitle === section.title && x.label === label)?.id] || {};
              const row = `
                <tr>
                  <td>${escapeHtml(itemCounter)}</td>
                  <td>${escapeHtml(label)}</td>
                  <td>${escapeHtml(entry.quantity_verified || '')}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'C'))}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'NC'))}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'NA'))}</td>
                  <td>${escapeHtml(entry.observations || '')}</td>
                </tr>
              `;
              itemCounter += 1;
              return row;
            }).join('');
            return `
              <tr><td class="section-cell" colspan="7">${escapeHtml(section.title.toUpperCase())}</td></tr>
              ${sectionRows}
            `;
          }).join('');
          return `
            <table>
              <colgroup>
                <col class="w-itemno" />
                <col class="w-elemento" />
                <col class="w-cant" />
                <col class="w-short" />
                <col class="w-short" />
                <col class="w-short" />
                <col class="w-observation" />
              </colgroup>
              <tr>
                <th class="header">ITEM</th>
                <th class="header">ELEMENTO</th>
                <th class="header">CANT. VERIFICADA</th>
                <th class="header">CUMPLE</th>
                <th class="header">NO CUMPLE</th>
                <th class="header">NO APLICA</th>
                <th class="header">OBSERVACIONES</th>
              </tr>
              ${body}
            </table>
          `;
        }

        if (inspectionType === 'saneamiento_basico') {
          const body = config.checklistSections.map((section) => {
            const sectionRows = section.items.map((label, idx) => {
              const entry = checklist[checklistEntries.find((x) => x.sectionTitle === section.title && x.label === label)?.id] || {};
              return `
                <tr>
                  <td>${idx === 0 ? escapeHtml(section.title) : ''}</td>
                  <td>${escapeHtml(label)}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'C'))}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'NC'))}</td>
                  <td class="center">${escapeHtml(markX(entry.status, 'NA'))}</td>
                  <td>${escapeHtml(entry.observations || '')}</td>
                </tr>
              `;
            }).join('');
            return sectionRows;
          }).join('');
          return `
            <table>
              <colgroup>
                <col class="w-section" />
                <col class="w-question" />
                <col class="w-short" />
                <col class="w-short" />
                <col class="w-short" />
                <col class="w-observation" />
              </colgroup>
              <tr>
                <th class="header">ITEM</th>
                <th class="header">DESCRIPCIÓN</th>
                <th class="header">C</th>
                <th class="header">NC</th>
                <th class="header">NA</th>
                <th class="header">OBSERVACIONES</th>
              </tr>
              ${body}
            </table>
          `;
        }

        const body = config.checklistSections.map((section) => {
          const sectionRows = section.items.map((label) => {
            const entry = checklist[checklistEntries.find((x) => x.sectionTitle === section.title && x.label === label)?.id] || {};
            return `
              <tr>
                <td>${escapeHtml(label)}</td>
                <td class="center">${escapeHtml(markX(entry.status, 'C'))}</td>
                <td class="center">${escapeHtml(markX(entry.status, 'NC'))}</td>
                <td class="center">${escapeHtml(markX(entry.status, 'NA'))}</td>
                <td>${escapeHtml(entry.observations || '')}</td>
              </tr>
            `;
          }).join('');
          return section.title !== 'Aspectos a evaluar'
            ? `<tr><td class="section-cell" colspan="5">${escapeHtml(section.title.toUpperCase())}</td></tr>${sectionRows}`
            : sectionRows;
        }).join('');
        return `
          <table>
            <colgroup>
              <col class="w-question" />
              <col class="w-short" />
              <col class="w-short" />
              <col class="w-short" />
              <col class="w-observation" />
            </colgroup>
            <tr>
              <th class="header">${inspectionType === 'respel' ? 'ASPECTO A EVALUAR' : 'ASPECTO A EVALUAR'}</th>
              <th class="header">C</th>
              <th class="header">NC</th>
              <th class="header">NA</th>
              <th class="header">OBSERVACIONES</th>
            </tr>
            ${body}
          </table>
        `;
      };

      const buildFooter = (metadata) => {
        if (inspectionType === 'sustancias_quimicas') {
          return `
            <table>
              <tr><td class="meta-label">RESPONSABLE DE INSPECCION:</td><td class="meta-value" colspan="5"></td></tr>
              <tr><td class="meta-label">NOMBRE:</td><td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'responsible_name'))}</td></tr>
              <tr><td class="meta-label">CARGO:</td><td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'responsible_role'))}</td></tr>
              <tr><td class="meta-label">FIRMA:</td><td class="meta-value" colspan="5"></td></tr>
            </table>
          `;
        }
        if (inspectionType === 'saneamiento_basico') {
          return `
            <table>
              <tr>
                <td class="meta-label" colspan="4">NOMBRE Y CARGO DE QUIEN REALIZA LA INSPECCIÓN</td>
                <td class="meta-value" colspan="4">${escapeHtml(metadataValue(metadata, 'inspector_name_role'))}</td>
                <td class="meta-label">FIRMA</td>
                <td class="meta-value" colspan="2"></td>
              </tr>
            </table>
          `;
        }
        if (inspectionType === 'kit_derrames') {
          return `
            <table>
              <tr>
                <td class="meta-label" colspan="3">REALIZADA POR</td>
                <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'performed_by_name'))}</td>
                <td class="meta-label">CARGO</td>
                <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'performed_by_role'))}</td>
                <td class="meta-label">FIRMA</td>
                <td class="meta-value" colspan="1"></td>
              </tr>
              <tr>
                <td class="meta-label" colspan="3">RECIBIDA POR</td>
                <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'received_by_name'))}</td>
                <td class="meta-label">CARGO</td>
                <td class="meta-value" colspan="2">${escapeHtml(metadataValue(metadata, 'received_by_role'))}</td>
                <td class="meta-label">FIRMA</td>
                <td class="meta-value" colspan="1"></td>
              </tr>
            </table>
          `;
        }
        return `
          <table>
            <tr>
              <td class="meta-label" colspan="3">INSPECCION REALIZADA POR:</td>
              <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'performed_by_name'))}</td>
              <td class="meta-label" colspan="3">INSPECCION RECIBIDA POR:</td>
              <td class="meta-value" colspan="3">${escapeHtml(metadataValue(metadata, 'received_by_name'))}</td>
            </tr>
            <tr>
              <td class="meta-label">NOMBRE:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'performed_by_name'))}</td>
              <td class="meta-label">NOMBRE:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'received_by_name'))}</td>
            </tr>
            <tr>
              <td class="meta-label">CARGO:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'performed_by_role'))}</td>
              <td class="meta-label">CARGO:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'received_by_role'))}</td>
            </tr>
            <tr>
              <td class="meta-label">CEDULA:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'performed_by_id'))}</td>
              <td class="meta-label">CEDULA:</td>
              <td class="meta-value" colspan="5">${escapeHtml(metadataValue(metadata, 'received_by_id'))}</td>
            </tr>
            <tr>
              <td class="meta-label">FIRMA:</td>
              <td class="meta-value" colspan="5"></td>
              <td class="meta-label">FIRMA:</td>
              <td class="meta-value" colspan="5"></td>
            </tr>
          </table>
        `;
      };

      const documentsHtml = rows.map((row) => {
        const metadata = parseJsonObject(row.metadata_json || row.metadata);
        const checklist = parseJsonObject(row.checklist_json || row.checklist);
        const counts = countStatuses(checklist);
        return `
          <div class="page">
            <table class="header-table">
              <tr>
                <td class="logo-cell" colspan="2" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="6">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label">CODIGO:</td>
                <td class="meta-value">${escapeHtml(config.formCode)}</td>
              </tr>
              <tr>
                <td class="title" colspan="6">SISTEMA DE GESTION AMBIENTAL</td>
                <td class="meta-label">VERSION:</td>
                <td class="meta-value">${escapeHtml(config.version)}</td>
              </tr>
              <tr>
                <td class="title" colspan="6">${escapeHtml(config.title.toUpperCase())}</td>
                <td class="meta-label">${escapeHtml(config.headerDateLabel || 'FECHA:')}</td>
                <td class="meta-value">${escapeHtml(fmtDateDisplay(config.headerDateValue || todayYmd()))}</td>
              </tr>
              ${buildMetaRows(metadata)}
              <tr>
                <td class="meta-note" colspan="10">Marque con una (X) la casilla correspondiente de acuerdo con lo observado en la inspección C: Cumple, NC: No Cumple, NA: No Aplica</td>
              </tr>
            </table>

            ${buildChecklistTable(checklist)}

            <table>
              <tr>
                <td class="meta-label">RESUMEN</td>
                <td class="meta-value">C: ${escapeHtml(counts.C)} | NC: ${escapeHtml(counts.NC)} | NA: ${escapeHtml(counts.NA)}</td>
                <td class="meta-label">OBSERVACIONES GENERALES</td>
                <td class="meta-value" colspan="3">${escapeHtml(row.notes || '')}</td>
              </tr>
            </table>

            ${buildFooter(metadata)}
          </div>
        `;
      }).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>${escapeHtml(config.title.slice(0, 31))}</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; margin-bottom: 12px; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 10pt; vertical-align: middle; word-wrap: break-word; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; width: 160px; }
              .meta-value { text-align: left; }
              .meta-note { font-weight: 700; text-align: left; background: #fff; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .section-cell { background: #f3f4f6; font-weight: 700; text-align: left; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 180px; max-height: 70px; }
              .center { text-align: center; }
              .page { page-break-after: always; margin-bottom: 24px; }
              .header-table td { background: #fff; }
              .w-section { width: 170px; }
              .w-itemno { width: 60px; }
              .w-short { width: 60px; }
              .w-elemento { width: 250px; }
              .w-question { width: 420px; }
              .w-cant { width: 120px; }
              .w-observation { width: 220px; }
            </style>
          </head>
          <body>
            ${documentsHtml || '<table><tr><td>Sin registros para exportar</td></tr></table>'}
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeType = inspectionType.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      link.href = url;
      link.download = `inspeccion_${safeType}_${(location || 'sede').replace(/[^a-z0-9]+/gi, '_')}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Inspección ambiental' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando inspección ambiental', err);
      setFeedback('No se pudo exportar el archivo Excel.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm"
            style={{ background: config.gradient }}
          >
            <config.icon size={20} className="text-white" />
          </div>
          <div>
            <div className="text-base font-semibold text-gray-800">{config.title}</div>
            <div className="text-sm text-gray-600">{config.description}</div>
            <div className="text-xs text-gray-500 mt-1">
              Sede: {locationLabel} | Código: {config.formCode} | Versión: {config.version} | {String(config.headerDateLabel || 'FECHA:').replace(/:$/, '')}: {fmtDateDisplay(config.headerDateValue || '')}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {!IS_SIG_MOBILE && (
            <button
              type="button"
              onClick={exportExcel}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110"
              style={{ background: config.gradient, borderColor: '#0f766e' }}
            >
              <Download size={16} /> Exportar Excel
            </button>
          )}
          <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1">C: {statusSummary.C}</span>
          <span className="rounded-full bg-red-50 border border-red-200 text-red-700 px-3 py-1">NC: {statusSummary.NC}</span>
          <span className="rounded-full bg-gray-50 border border-gray-300 text-gray-700 px-3 py-1">NA: {statusSummary.NA}</span>
        </div>
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-5">
        {config.fieldGroups.map((group) => (
          <div key={group.title} className="space-y-3">
            <div className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">{group.title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {group.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">{field.label}{field.required ? ' *' : ''}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      className="w-full border rounded px-3 py-2 min-h-[90px]"
                      value={form.metadata[field.key] || ''}
                      onChange={(e) => onMetadataChange(field.key, e.target.value)}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      className="w-full border rounded px-3 py-2"
                      value={form.metadata[field.key] || ''}
                      onChange={(e) => onMetadataChange(field.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="space-y-4">
          <div className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Lista de chequeo</div>
          {config.checklistSections.map((section, sectionIndex) => (
            <div key={section.title} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800">{section.title}</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left w-12">#</th>
                      <th className="px-3 py-2 text-left">Pregunta</th>
                      {inspectionType === 'kit_derrames' ? <th className="px-3 py-2 text-left w-36">Cant. verificada</th> : null}
                      <th className="px-3 py-2 text-left w-44">Cumplimiento</th>
                      <th className="px-3 py-2 text-left">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.items.map((label, itemIndex) => {
                      const entry = checklistEntries.find((item) => item.sectionTitle === section.title && item.label === label);
                      const value = form.checklist[entry?.id] || { status: '', observations: '' };
                      return (
                        <tr key={entry?.id || `${section.title}-${itemIndex}`} className="border-t align-top">
                          <td className="px-3 py-2 text-gray-500">{sectionIndex + 1}.{itemIndex + 1}</td>
                          <td className="px-3 py-2">{label}</td>
                          {inspectionType === 'kit_derrames' ? (
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                className="w-full border rounded px-2 py-1"
                                value={value.quantity_verified || ''}
                                onChange={(e) => onChecklistChange(entry.id, { quantity_verified: e.target.value })}
                                placeholder="Cantidad / detalle"
                              />
                            </td>
                          ) : null}
                          <td className="px-3 py-2">
                            <select
                              className="w-full border rounded px-2 py-2 bg-white"
                              value={value.status || ''}
                              onChange={(e) => onChecklistChange(entry.id, { status: e.target.value })}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            {inspectionType === 'kit_derrames' ? (
                              <input
                                type="text"
                                className="w-full border rounded px-2 py-1"
                                value={value.observations || ''}
                                onChange={(e) => onChecklistChange(entry.id, { observations: e.target.value })}
                                placeholder="Observaciones"
                              />
                            ) : (
                              <input
                                type="text"
                                className="w-full border rounded px-2 py-1"
                                value={value.observations || ''}
                                onChange={(e) => onChecklistChange(entry.id, { observations: e.target.value })}
                                placeholder="Observaciones"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Observaciones generales</label>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-[90px]"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </div>

        {feedback ? (
          <div className="text-sm rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2">{feedback}</div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setForm(emptyInspectionForm(config));
              setFeedback('');
            }}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-300"
          >
            Limpiar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110 disabled:opacity-60"
            style={{ background: config.gradient, borderColor: '#0f766e' }}
          >
            <Save size={16} /> {saving ? 'Guardando...' : 'Guardar inspección'}
          </button>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-gray-800">Registros guardados</div>
            <div className="text-xs text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros en ${config.title}`}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                {config.listColumns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left">{col.label}</th>
                ))}
                <th className="px-3 py-2 text-left">C</th>
                <th className="px-3 py-2 text-left">NC</th>
                <th className="px-3 py-2 text-left">NA</th>
                <th className="px-3 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const metadata = parseJsonObject(row.metadata_json || row.metadata);
                const checklist = parseJsonObject(row.checklist_json || row.checklist);
                const counts = countStatuses(checklist);
                return (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">{row.id}</td>
                    {config.listColumns.map((col) => (
                      <td key={col.key} className="px-3 py-2">
                        {col.key === 'inspection_date' ? fmtDateDisplay(metadata[col.key] || row.inspection_date || '') : (metadata[col.key] || '-')}
                      </td>
                    ))}
                    <td className="px-3 py-2">{counts.C}</td>
                    <td className="px-3 py-2">{counts.NC}</td>
                    <td className="px-3 py-2">{counts.NA}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                          <Pencil size={14} /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-gray-500" colSpan={config.listColumns.length + 5}>No hay registros guardados para esta inspección.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
